import {
  defaultRangeExtractor,
  observeElementRect,
  useVirtualizer,
} from '@tanstack/react-virtual';
import { useCallback, useRef } from 'react';
import type { RefObject } from 'react';
import type { ChatRenderItem } from '../components/chat-message-list-grouping';
import { getStableKeyForRenderItem } from '../utils/chat-render-item-keys';
const ESTIMATED_ROW_HEIGHT = 80;

type VisibilityCheck = (options?: {
  checkOpacity?: boolean;
  checkVisibilityCSS?: boolean;
}) => boolean;

export function isChatRowVisible(element: HTMLElement): boolean {
  const candidate = element as HTMLElement & {
    checkVisibility?: VisibilityCheck;
  };

  if (typeof candidate.checkVisibility === 'function') {
    return candidate.checkVisibility({
      checkOpacity: false,
      checkVisibilityCSS: true,
    });
  }

  return element.getClientRects().length > 0;
}

function readBorderBoxBlockSize(
  entry: ResizeObserverEntry | undefined,
): number {
  const blockSize = entry?.borderBoxSize?.[0]?.blockSize;

  if (blockSize === undefined || !Number.isFinite(blockSize)) {
    return 0;
  }

  return Math.round(blockSize);
}

export function measureChatRow({
  measuredHeight,
  cachedHeight,
  layoutHeight,
  canTrustLayout = true,
}: {
  measuredHeight: number;
  cachedHeight: number | undefined;
  layoutHeight?: number;
  canTrustLayout?: boolean;
}): number {
  if (!canTrustLayout) {
    if (cachedHeight !== undefined && cachedHeight > 0) {
      return cachedHeight;
    }

    return ESTIMATED_ROW_HEIGHT;
  }

  if (measuredHeight > 0) {
    return measuredHeight;
  }

  if (layoutHeight !== undefined && layoutHeight > 0) {
    return layoutHeight;
  }

  if (layoutHeight === 0) {
    return 0;
  }

  if (cachedHeight !== undefined && cachedHeight > 0) {
    return cachedHeight;
  }

  return ESTIMATED_ROW_HEIGHT;
}

export function useChatVirtualizer({
  items,
  scrollElementRef,
  stickyIndex = -1,
}: {
  items: ChatRenderItem[];
  scrollElementRef: RefObject<HTMLDivElement | null>;
  stickyIndex?: number;
}) {
  const activeStickyIndexRef = useRef(-1);

  const getItemKey = useCallback(
    (index: number) => {
      const item = items[index];

      if (!item) return `fallback-${index}`;

      return getStableKeyForRenderItem(item);
    },
    [items],
  );

  const rangeExtractor = useCallback(
    (range: Parameters<typeof defaultRangeExtractor>[0]) => {
      const defaultRange = defaultRangeExtractor(range);

      if (stickyIndex < 0) {
        activeStickyIndexRef.current = -1;

        return defaultRange;
      }

      const isActive = range.startIndex > stickyIndex;

      activeStickyIndexRef.current = isActive ? stickyIndex : -1;

      if (!isActive) {
        return defaultRange;
      }

      const withSticky = new Set(defaultRange);

      withSticky.add(stickyIndex);

      return [...withSticky].sort((a, b) => a - b);
    },
    [stickyIndex],
  );

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    initialRect: {
      width: scrollElementRef.current?.clientWidth ?? 0,
      height: scrollElementRef.current?.clientHeight || 600,
    },
    getItemKey,
    overscan: 5,
    rangeExtractor,
    measureElement: (element, entry, instance) => {
      const index = instance.indexFromElement(element);
      const key = instance.options.getItemKey(index);
      const cachedItem = instance.measurementsCache[index];
      const cachedHeight =
        cachedItem?.key === key ? cachedItem.size : undefined;
      const measuredHeight = readBorderBoxBlockSize(entry);
      const scroller = instance.scrollElement;
      const scrollerIsLaidOut =
        scroller instanceof HTMLElement && scroller.clientHeight > 0;
      const rowIsVisible =
        element instanceof HTMLElement &&
        element.isConnected &&
        isChatRowVisible(element);
      const canTrustLayout = scrollerIsLaidOut && rowIsVisible;

      if (!(element instanceof HTMLElement) || !canTrustLayout) {
        return measureChatRow({
          measuredHeight,
          cachedHeight,
          canTrustLayout: false,
        });
      }

      const layoutHeight = element.offsetHeight;

      return measureChatRow({
        measuredHeight,
        cachedHeight,
        layoutHeight: measuredHeight > 0 ? undefined : layoutHeight,
      });
    },
    observeElementRect: (instance, cb) =>
      observeElementRect(instance, (rect) => {
        if (rect.height <= 0) {
          return;
        }

        cb(rect);
      }),
  });

  return { virtualizer, activeStickyIndexRef };
}
